/** Memso "Weave" anchor.
 *
 *  React Flow canvas of the user's knowledge graph. Loads the
 *  user's anchor nodes on mount, lets them click into a node to
 *  expand its neighbourhood, drag to pan, scroll / pinch to zoom,
 *  search by label, filter by type, and adjust depth (1 / 2 / 3).
 *
 *  Brand voice: the user-facing copy uses "weave" and "connections"
 *  — never "brain" or "dump" (per the Memso anti-lexicon). */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Search,
  Plus,
  Sparkles,
  RefreshCw,
  Filter,
  Layers,
  Loader2,
  Network,
} from "lucide-react";
import ReactFlow, {
  Background,
  BackgroundVariant,
  Controls,
  MiniMap,
  type Edge,
  type Node,
  type ReactFlowInstance,
} from "reactflow";
import "reactflow/dist/style.css";
import {
  fetchAnchors,
  fetchNeighborhood,
  searchGraph,
  seedStarterGraph,
} from "./api";
import {
  edgeStyleFor,
  nodeStyleFor,
  NODE_TYPE_ORDER,
} from "./palette";
import { ViewFilter, useViewFilter } from "../../components/ViewFilter";
import { TeachingNote } from "../../components/TeachingNote";
import {
  constellationLayout,
  forceDirectedLayout,
  radialLayout,
  type LayoutMode,
  type PositionedNode,
} from "./layout";
import { nodeTypes, type WeaveNodeData } from "./nodeTypes";
import { DetailPanel } from "./DetailPanel";
import type { ApiEdge, ApiNode } from "./types";

type FetchState =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "ready" }
  | { kind: "error"; message: string };

const VIRTUALIZE_THRESHOLD = 200;

export function KnowledgeGraph() {
  const [state, setState] = useState<FetchState>({ kind: "idle" });
  const [nodes, setNodes] = useState<ApiNode[]>([]);
  const [edges, setEdges] = useState<ApiEdge[]>([]);
  const [counts, setCounts] = useState<Array<{ type: string; n: number }>>([]);
  const [stats, setStats] = useState<{ nodes: number; edges: number } | null>(null);
  const [rootId, setRootId] = useState<string | null>(null);
  const [depth, setDepth] = useState(2);
  const [activeTypes, setActiveTypes] = useState<Set<string>>(new Set());
  const [query, setQuery] = useState("");
  const [searchHits, setSearchHits] = useState<ApiNode[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [seeding, setSeeding] = useState(false);
  const [layoutMode, setLayoutMode] = useState<LayoutMode>("hierarchical");
  // "Show edges stronger than X" — hides dormant noise. Default 0.1
  // matches the spec; below this, weak/old edges drop out so the
  // canvas reads as live relationships, not 7-year-old vendors.
  const [minStrength, setMinStrength] = useState(0.1);
  const flowRef = useRef<ReactFlowInstance | null>(null);
  const { state: filter, setState: setFilter, loaded: filterLoaded } = useViewFilter("graph");

  const loadAnchors = useCallback(async () => {
    setState({ kind: "loading" });
    try {
      const a = await fetchAnchors(300, filter.includeTest);
      setNodes(a.nodes);
      setEdges(a.edges);
      setCounts(a.counts);
      setStats(a.stats);
      setRootId(null);
      setSelectedId(null);
      setState({ kind: "ready" });
    } catch (err) {
      setState({
        kind: "error",
        message: err instanceof Error ? err.message : String(err),
      });
    }
  }, []);

  const expandFromNode = useCallback(
    async (nodeId: string, d: number) => {
      setState({ kind: "loading" });
      try {
        const r = await fetchNeighborhood(nodeId, d, 200, filter.includeTest);
        setNodes(r.nodes);
        setEdges(r.edges);
        setRootId(r.rootId);
        setSelectedId(r.rootId);
        setState({ kind: "ready" });
      } catch (err) {
        setState({
          kind: "error",
          message: err instanceof Error ? err.message : String(err),
        });
      }
    },
    [filter.includeTest],
  );

  useEffect(() => {
    if (!filterLoaded) return;
    void loadAnchors();
  }, [loadAnchors, filterLoaded]);

  // Refresh on tab focus so a capture in another tab + ingestion cycle
  // surfaces here without a full reload.
  useEffect(() => {
    function onFocus() {
      void loadAnchors();
    }
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [loadAnchors]);

  // Re-run the neighbourhood walk when the depth slider moves AND we
  // have a root.
  useEffect(() => {
    if (rootId) void expandFromNode(rootId, depth);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [depth]);

  // Search debounce.
  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setSearchHits([]);
      return;
    }
    const handle = window.setTimeout(async () => {
      try {
        const r = await searchGraph(q, 12, filter.includeTest);
        setSearchHits(r.nodes);
      } catch {
        setSearchHits([]);
      }
    }, 180);
    return () => window.clearTimeout(handle);
  }, [query, filter.includeTest]);

  // Build the visible (filtered) node + edge sets for layout.
  const visibleNodes = useMemo(() => {
    if (activeTypes.size === 0) return nodes;
    return nodes.filter((n) => activeTypes.has(n.type));
  }, [nodes, activeTypes]);

  const visibleNodeIds = useMemo(
    () => new Set(visibleNodes.map((n) => n.id)),
    [visibleNodes],
  );

  const edgeStrength = (e: ApiEdge): number => {
    const s = (e.props as { strength?: unknown })?.strength;
    return typeof s === "number" && Number.isFinite(s) ? s : 0;
  };

  const visibleEdges = useMemo(
    () =>
      edges.filter(
        (e) =>
          visibleNodeIds.has(e.from) &&
          visibleNodeIds.has(e.to) &&
          // Unscored edges (strength absent) stay visible until the
          // scorer runs; only hide edges we know are below threshold.
          (((e.props as { strength?: unknown })?.strength == null) ||
            edgeStrength(e) >= minStrength),
      ),
    [edges, visibleNodeIds, minStrength],
  );

  // Adaptive node-size denominator: log-scale total_strength against
  // the strongest visible node so the spread reads on this canvas.
  const strengthMax = useMemo(() => {
    let m = 0;
    for (const n of nodes) {
      const t = (n.props as { total_strength?: unknown })?.total_strength;
      if (typeof t === "number" && t > m) m = t;
    }
    return m;
  }, [nodes]);

  // Lay out nodes. When > VIRTUALIZE_THRESHOLD, fall back to a denser
  // grid so React Flow virtualises smoothly.
  const positioned = useMemo<PositionedNode[]>(() => {
    if (visibleNodes.length === 0) return [];
    if (visibleNodes.length > VIRTUALIZE_THRESHOLD) {
      // Grid for huge graphs: don't try to pretty-layout, react-flow
      // will virtualise off-screen nodes for us.
      const cols = Math.ceil(Math.sqrt(visibleNodes.length));
      return visibleNodes.map((n, i) => ({
        ...n,
        x: (i % cols) * 220 - (cols * 220) / 2,
        y: Math.floor(i / cols) * 100,
      }));
    }
    if (layoutMode === "force") {
      return forceDirectedLayout(visibleNodes, visibleEdges);
    }
    if (rootId && visibleNodes.some((n) => n.id === rootId)) {
      return radialLayout(visibleNodes, visibleEdges, rootId);
    }
    return constellationLayout(visibleNodes);
  }, [visibleNodes, visibleEdges, rootId, layoutMode]);

  const nodesById = useMemo(() => {
    const m = new Map<string, ApiNode>();
    for (const n of nodes) m.set(n.id, n);
    return m;
  }, [nodes]);

  const flowNodes = useMemo<Node<WeaveNodeData>[]>(() => {
    const denom = Math.log1p(Math.max(strengthMax, 0.0001));
    return positioned.map((p) => {
      const src = nodesById.get(p.id);
      const ts = (src?.props as { total_strength?: unknown })?.total_strength;
      const tsNum = typeof ts === "number" && Number.isFinite(ts) ? ts : 0;
      // Log-scaled 8..32 px accent, relative to the strongest node.
      const frac = denom > 0 ? Math.log1p(tsNum) / denom : 0;
      const size = Math.round(8 + 24 * Math.min(1, Math.max(0, frac)));
      return {
        id: p.id,
        type: "weave",
        position: { x: p.x, y: p.y },
        data: {
          label: p.label,
          type: p.type,
          selected: selectedId === p.id,
          size,
          totalStrength: tsNum,
        },
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [positioned, selectedId, strengthMax, nodesById]);

  const flowEdges = useMemo<Edge[]>(() => {
    return visibleEdges.map((e) => {
      const style = edgeStyleFor(e.type);
      const sRaw = (e.props as { strength?: unknown })?.strength;
      const hasStrength = typeof sRaw === "number" && Number.isFinite(sRaw);
      const s = hasStrength ? Math.min(1, Math.max(0, sRaw as number)) : null;
      // thickness proportional to strength*5 px (min 0.5, max 5);
      // opacity 0.2 + strength*0.8. Unscored edges keep the neutral
      // 1.5 px until the scorer runs.
      const strokeWidth = s == null ? 1.5 : Math.min(5, Math.max(0.5, s * 5));
      const opacity = s == null ? 0.7 : 0.2 + s * 0.8;
      return {
        id: e.id,
        source: e.from,
        target: e.to,
        label: e.type,
        type: "smoothstep",
        animated: e.type === "blocks",
        style: {
          stroke: style.stroke,
          strokeWidth,
          opacity,
          strokeDasharray: style.strokeDasharray,
        },
        labelStyle: {
          fontSize: 10,
          fill: style.stroke,
          fontWeight: 600,
        },
        labelBgPadding: [4, 2],
        labelBgBorderRadius: 4,
        labelBgStyle: {
          fill: "rgba(255,255,255,0.85)",
          fillOpacity: 0.85,
        },
      };
    });
  }, [visibleEdges]);

  const selectedNode = selectedId ? nodesById.get(selectedId) ?? null : null;

  const toggleType = useCallback((type: string) => {
    setActiveTypes((prev) => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      return next;
    });
  }, []);

  const handleNodeClick = useCallback(
    (_evt: React.MouseEvent, node: Node<WeaveNodeData>) => {
      setSelectedId(node.id);
    },
    [],
  );

  const handleNodeDoubleClick = useCallback(
    (_evt: React.MouseEvent, node: Node<WeaveNodeData>) => {
      void expandFromNode(node.id, depth);
    },
    [expandFromNode, depth],
  );

  const handleSeed = useCallback(async () => {
    setSeeding(true);
    try {
      await seedStarterGraph();
      await loadAnchors();
    } catch (err) {
      setState({
        kind: "error",
        message: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setSeeding(false);
    }
  }, [loadAnchors]);

  const isEmpty = state.kind === "ready" && nodes.length === 0;
  const isLoading = state.kind === "loading" || state.kind === "idle";

  // Render order:
  //  - Empty state: brand-voice placeholder + Capture / Seed CTAs.
  //  - Loading state: skeleton shimmer.
  //  - Ready state: full canvas with toolbar + detail panel.
  return (
    <section className="space-y-3">
      {!isEmpty && (
        <TeachingNote
          id="weave.v1"
          tone="sky"
          title="Weave shows how everything you've captured connects."
          body="Click a node to expand its neighbourhood, drag to pan, scroll to zoom. Filter by type to surface just people, places, projects, or notes. Depth controls how many hops out from the focused node Memso renders."
        />
      )}
      <div className="glass rounded-2xl border border-slate-200/70 dark:border-slate-700/50 backdrop-blur overflow-hidden">
      <Toolbar
        query={query}
        onQuery={setQuery}
        searchHits={searchHits}
        onSearchHit={(id) => {
          setQuery("");
          setSearchHits([]);
          void expandFromNode(id, depth);
        }}
        depth={depth}
        onDepth={setDepth}
        minStrength={minStrength}
        onMinStrength={setMinStrength}
        counts={counts}
        activeTypes={activeTypes}
        onToggleType={toggleType}
        onClearTypes={() => setActiveTypes(new Set())}
        onRefresh={loadAnchors}
        layoutMode={layoutMode}
        onLayoutMode={setLayoutMode}
        rootLabel={rootId ? nodesById.get(rootId)?.label ?? null : null}
        onClearRoot={() => {
          setRootId(null);
          void loadAnchors();
        }}
        filter={filter}
        onFilter={setFilter}
      />

      {state.kind === "error" && (
        <div className="m-4 rounded-xl border border-rose-200 dark:border-rose-700 bg-rose-50 dark:bg-rose-950/40 px-3 py-2 text-sm text-rose-700 dark:text-rose-300">
          {state.message}
        </div>
      )}

      <div className="flex flex-row min-h-[60vh] max-h-[70vh] relative">
        <div className="flex-1 min-w-0 relative">
          {isLoading && <CanvasSkeleton />}
          {isEmpty && !isLoading && <EmptyState onSeed={handleSeed} seeding={seeding} />}
          {!isEmpty && (
            <ReactFlow
              nodes={flowNodes}
              edges={flowEdges}
              nodeTypes={nodeTypes}
              onNodeClick={handleNodeClick}
              onNodeDoubleClick={handleNodeDoubleClick}
              onPaneClick={() => setSelectedId(null)}
              onInit={(inst) => {
                flowRef.current = inst;
              }}
              fitView
              fitViewOptions={{ padding: 0.2, maxZoom: 1.4 }}
              minZoom={0.2}
              maxZoom={2.5}
              proOptions={{ hideAttribution: true }}
              defaultEdgeOptions={{ type: "smoothstep" }}
              nodesDraggable
              panOnScroll
              zoomOnPinch
              elementsSelectable
            >
              <Background variant={BackgroundVariant.Dots} gap={24} size={1} className="opacity-40" />
              <Controls position="bottom-right" showInteractive={false} />
              {flowNodes.length > 30 && (
                <MiniMap
                  pannable
                  zoomable
                  className="!bg-white/80 dark:!bg-slate-900/80 !border !border-slate-200 dark:!border-slate-700 rounded-md"
                  nodeColor={(n) => nodeStyleFor((n.data as WeaveNodeData)?.type ?? "").accent}
                />
              )}
            </ReactFlow>
          )}
        </div>

        {selectedNode && (
          <DetailPanel
            node={selectedNode}
            edges={edges}
            nodesById={nodesById}
            onClose={() => setSelectedId(null)}
            onSelect={(id) => {
              setSelectedId(id);
              const n = nodesById.get(id);
              if (n && rootId !== id) void expandFromNode(id, depth);
            }}
          />
        )}
      </div>

      <Footer stats={stats} visibleCount={visibleNodes.length} edgeCount={visibleEdges.length} />
      </div>
    </section>
  );
}

function Toolbar({
  query,
  onQuery,
  searchHits,
  onSearchHit,
  depth,
  onDepth,
  minStrength,
  onMinStrength,
  counts,
  activeTypes,
  onToggleType,
  onClearTypes,
  onRefresh,
  layoutMode,
  onLayoutMode,
  rootLabel,
  onClearRoot,
  filter,
  onFilter,
}: {
  query: string;
  onQuery: (s: string) => void;
  searchHits: ApiNode[];
  onSearchHit: (id: string) => void;
  depth: number;
  onDepth: (d: number) => void;
  minStrength: number;
  onMinStrength: (n: number) => void;
  counts: Array<{ type: string; n: number }>;
  activeTypes: Set<string>;
  onToggleType: (type: string) => void;
  onClearTypes: () => void;
  onRefresh: () => void;
  layoutMode: LayoutMode;
  onLayoutMode: (m: LayoutMode) => void;
  rootLabel: string | null;
  onClearRoot: () => void;
  filter: ReturnType<typeof useViewFilter>["state"];
  onFilter: ReturnType<typeof useViewFilter>["setState"];
}) {
  const ordered = useMemo(() => orderTypes(counts), [counts]);
  return (
    <header className="border-b border-slate-200/70 dark:border-slate-700/40 px-3 py-2.5 space-y-2">
      <div className="flex items-center gap-2 flex-wrap">
        <h2 className="text-base font-bold text-slate-900 dark:text-slate-100 inline-flex items-center gap-1.5 mr-1">
          <Network className="w-4 h-4 text-sky-600 dark:text-sky-400" />
          Weave
        </h2>

        <ViewFilter state={filter} onChange={onFilter} />

        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
          <input
            type="search"
            value={query}
            onChange={(e) => onQuery(e.target.value)}
            placeholder="Search the weave"
            aria-label="Search nodes"
            className="w-full pl-8 pr-2 py-1.5 text-sm rounded-lg bg-white/80 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-400/40"
          />
          {searchHits.length > 0 && (
            <ul
              role="listbox"
              className="absolute top-full left-0 right-0 mt-1 z-20 max-h-72 overflow-y-auto rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-xl"
            >
              {searchHits.map((n) => {
                const style = nodeStyleFor(n.type);
                return (
                  <li key={n.id}>
                    <button
                      type="button"
                      role="option"
                      onClick={() => onSearchHit(n.id)}
                      className="w-full text-left flex items-center gap-2 px-3 py-2 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                    >
                      <span
                        aria-hidden
                        className="w-2 h-2 rounded-full flex-shrink-0"
                        style={{ background: style.accent }}
                      />
                      <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400 flex-shrink-0">
                        {n.type}
                      </span>
                      <span className="text-sm text-slate-800 dark:text-slate-100 truncate">
                        {n.label}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="ml-auto flex items-center gap-2">
          <LayoutToggle mode={layoutMode} onChange={onLayoutMode} />
          <StrengthSlider value={minStrength} onChange={onMinStrength} />
          <DepthSlider depth={depth} onChange={onDepth} />
          <button
            type="button"
            onClick={onRefresh}
            className="inline-flex items-center gap-1 text-xs font-semibold text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-slate-100 px-2 py-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800/40"
            aria-label="Reload anchors"
            title="Reload"
          >
            <RefreshCw className="w-3.5 h-3.5" />
            Refresh
          </button>
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400 inline-flex items-center gap-1">
          <Filter className="w-3 h-3" /> Types
        </span>
        <Chip
          label="all"
          active={activeTypes.size === 0}
          onClick={onClearTypes}
        />
        {ordered.map((t) => {
          const style = nodeStyleFor(t.type);
          const active = activeTypes.has(t.type);
          return (
            <Chip
              key={t.type}
              label={`${t.type}${t.n != null ? ` · ${t.n}` : ""}`}
              active={active}
              onClick={() => onToggleType(t.type)}
              activeCls={style.chipActive}
              inactiveCls={style.chipInactive}
              dot={style.accent}
            />
          );
        })}
        {rootLabel && (
          <button
            type="button"
            onClick={onClearRoot}
            className="ml-auto text-[10px] uppercase tracking-wider font-bold text-sky-700 dark:text-sky-300 hover:underline px-2 py-1"
          >
            ← back to anchors ({rootLabel})
          </button>
        )}
      </div>
    </header>
  );
}

function LayoutToggle({
  mode,
  onChange,
}: {
  mode: LayoutMode;
  onChange: (m: LayoutMode) => void;
}) {
  const opts: Array<{ id: LayoutMode; label: string; title: string }> = [
    { id: "hierarchical", label: "Tree", title: "Hierarchical layout (concentric shells from a root)" },
    { id: "force", label: "Cluster", title: "Force-directed layout (clusters by connectivity)" },
  ];
  return (
    <div
      className="inline-flex items-center gap-1 text-xs"
      role="radiogroup"
      aria-label="Layout mode"
    >
      {opts.map((o) => {
        const active = mode === o.id;
        return (
          <button
            key={o.id}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onChange(o.id)}
            title={o.title}
            className={`px-2 py-1.5 rounded-lg font-semibold transition-colors ${
              active
                ? "bg-violet-600 text-white"
                : "bg-slate-100 dark:bg-slate-800/40 text-slate-700 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-700"
            }`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

function StrengthSlider({
  value,
  onChange,
}: {
  value: number;
  onChange: (n: number) => void;
}) {
  return (
    <div
      className="inline-flex items-center gap-1.5 text-xs"
      title="Hide edges weaker than this. Strength = how active + recent the relationship is."
    >
      <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400">
        Min strength
      </span>
      <input
        type="range"
        min={0}
        max={1}
        step={0.05}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        aria-label="Show edges stronger than"
        className="w-24 accent-sky-600"
      />
      <span className="tabular-nums font-semibold text-slate-600 dark:text-slate-300 w-8">
        {value.toFixed(2)}
      </span>
    </div>
  );
}

function DepthSlider({ depth, onChange }: { depth: number; onChange: (d: number) => void }) {
  return (
    <div
      className="inline-flex items-center gap-1.5 text-xs"
      title="Neighbourhood depth (only used when a node is expanded)"
    >
      <Layers className="w-3.5 h-3.5 text-slate-400" />
      <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400">
        Depth
      </span>
      {[1, 2, 3].map((d) => {
        const active = d === depth;
        return (
          <button
            key={d}
            type="button"
            onClick={() => onChange(d)}
            aria-pressed={active}
            className={`w-7 h-7 rounded-lg text-xs font-bold transition-colors ${
              active
                ? "bg-sky-600 text-white"
                : "bg-slate-100 dark:bg-slate-800/40 text-slate-700 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-700"
            }`}
          >
            {d}
          </button>
        );
      })}
    </div>
  );
}

function Chip({
  label,
  active,
  onClick,
  activeCls,
  inactiveCls,
  dot,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
  activeCls?: string;
  inactiveCls?: string;
  dot?: string;
}) {
  const a = activeCls ?? "bg-sky-600 text-white border-sky-600";
  const i =
    inactiveCls ??
    "bg-slate-100 dark:bg-slate-800/40 text-slate-700 dark:text-slate-200 border-slate-200 dark:border-slate-700";
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full border transition-colors ${active ? a : i}`}
    >
      {dot && (
        <span
          aria-hidden
          className="w-1.5 h-1.5 rounded-full flex-shrink-0"
          style={{ background: dot }}
        />
      )}
      {label}
    </button>
  );
}

function Footer({
  stats,
  visibleCount,
  edgeCount,
}: {
  stats: { nodes: number; edges: number } | null;
  visibleCount: number;
  edgeCount: number;
}) {
  return (
    <footer className="px-3 py-2 border-t border-slate-200/70 dark:border-slate-700/40 text-[10px] uppercase tracking-wider font-bold text-slate-400 dark:text-slate-500 flex items-center justify-between">
      <span>
        Showing {visibleCount} {visibleCount === 1 ? "node" : "nodes"} · {edgeCount}{" "}
        {edgeCount === 1 ? "connection" : "connections"}
      </span>
      {stats && (
        <span>
          Total weave · {stats.nodes} {stats.nodes === 1 ? "node" : "nodes"} · {stats.edges}{" "}
          {stats.edges === 1 ? "connection" : "connections"}
        </span>
      )}
    </footer>
  );
}

function EmptyState({ onSeed, seeding }: { onSeed: () => void; seeding: boolean }) {
  return (
    <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6 py-10 gap-3">
      <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-sky-400 to-violet-500 flex items-center justify-center shadow-lg">
        <Sparkles className="w-7 h-7 text-white" />
      </div>
      <h3 className="text-lg font-bold text-slate-900 dark:text-slate-100">
        Your weave is just starting.
      </h3>
      <p className="text-sm text-slate-500 dark:text-slate-400 max-w-sm">
        Capture a thought, a person, a place. Connections appear as Memso links them
        together.
      </p>
      <div className="flex items-center gap-2 mt-2">
        <Link
          to="/capture"
          className="inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-2 rounded-xl bg-emerald-600 text-white hover:bg-emerald-500 transition-colors shadow-md"
        >
          <Plus className="w-4 h-4" />
          Capture something
        </Link>
        <button
          type="button"
          onClick={onSeed}
          disabled={seeding}
          className="inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-2 rounded-xl bg-slate-100 dark:bg-slate-800/50 text-slate-700 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors disabled:opacity-50"
        >
          {seeding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          {seeding ? "Building…" : "Build my weave"}
        </button>
      </div>
    </div>
  );
}

function CanvasSkeleton() {
  return (
    <div className="absolute inset-0 flex items-center justify-center" aria-hidden>
      <div className="grid grid-cols-3 gap-4 opacity-60">
        {Array.from({ length: 9 }).map((_, i) => (
          <div
            key={i}
            className="w-32 h-12 rounded-xl bg-gradient-to-r from-slate-200 via-slate-100 to-slate-200 dark:from-slate-700 dark:via-slate-800 dark:to-slate-700 animate-pulse"
            style={{ animationDelay: `${i * 80}ms` }}
          />
        ))}
      </div>
    </div>
  );
}

function orderTypes(
  counts: Array<{ type: string; n: number }>,
): Array<{ type: string; n: number }> {
  const orderIdx = new Map<string, number>();
  NODE_TYPE_ORDER.forEach((t, i) => orderIdx.set(t, i));
  return [...counts].sort((a, b) => {
    const ai = orderIdx.has(a.type) ? orderIdx.get(a.type)! : 1000;
    const bi = orderIdx.has(b.type) ? orderIdx.get(b.type)! : 1000;
    if (ai !== bi) return ai - bi;
    return a.type.localeCompare(b.type);
  });
}

export default KnowledgeGraph;
